import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { GainsFormItemComponent } from '../gains-form-item/gains-form-item.component';
import { GainsGridUtil } from '../../util/gainsGridUtil';

@Component({
  selector: 'gains-input',
  standalone: false,
  templateUrl: './gains-input.component.html',
  styleUrl: './gains-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GainsInputComponent),
      multi: true
    }
  ]
})
export class GainsInputComponent extends GainsFormItemComponent implements ControlValueAccessor, OnInit {
  private static idCounter = 0;
  @Input() showAddOnBefore = false;
  @Input() selectOptions: { label: string; value: any }[] = [];
  @Input() prefixSelectedValue: any;
  @Input() type = 'text';
  @Input() suffixIconLink = '';
  @Input() suffixText = '';
  @Input() maxlength?: number | string;
  @Input() numericMode: NumericMode = 'decimal'; // 'decimal' | 'integer' | 'percentage' | 'currency'
  /** Bounds of a number, checked and explained by the input itself (the parent form keeps its own validators). */
  @Input() min?: number;
  @Input() max?: number;
  /**
   * A number out of min/max is brought back within them when the input is left, as nz-input-number does, rather than
   * shown as an error.
   */
  @Input() clampToBounds = false;
  /** The step of a number: `0.1` for decimals, `1` by default. */
  @Input() step?: number;
  /** The lines a `type="textarea"` shows. */
  @Input() rows = 3;
  /** The id of its input, for a `<label for>`: unique per input unless given. */
  @Input() inputId = `gains-input-${GainsInputComponent.idCounter++}`;
  /** Names the input for screen readers when it has no label. */
  @Input() ariaLabel?: string;
  /** The browser's autofill hint, such as `new-password` for a secret that must not be filled with a saved password. */
  @Input() autocomplete?: string;
  /** False keeps the browser from marking spelling, such as in a key or a code. */
  @Input() spellcheck?: boolean;
  @Output() valueChange = new EventEmitter<any>();
  gainsGridUtil = new GainsGridUtil();
  private onTouched: () => void = () => undefined;
  /** The number the form holds, for a number input left unfinished. */
  private _lastNumber: number | null = null;

  constructor() {
    super();
  }
  ngOnInit(): void {
    this.setControl();

    // Keep built-in email validation on the internal control so gains-input
    // can render its own email error message when used with type="email".
    if (this.type === 'email') {
      this.internalFormControl.addValidators(Validators.email);
      this.internalFormControl.updateValueAndValidity({ emitEvent: false });
    }

    // A clamped number is brought within its bounds instead: never out of them, never in error
    if (this.min != null && !this.clampToBounds) {
      this.internalFormControl.addValidators(Validators.min(this.min));
    }
    if (this.max != null && !this.clampToBounds) {
      this.internalFormControl.addValidators(Validators.max(this.max));
    }
    if (this.min != null || this.max != null) {
      this.internalFormControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  private onChange: (value: any) => void = (value: any) => {
    this.valueChange.emit(value);
  };

  /**
   * A number input gives the form a number (null when empty), as nz-input-number did; any other its text. A number
   * still being typed (`1.`, `-`) is only shown: the browser reads it as empty, which is not what the user means.
   */
  onValueChange(selectedValue: any, input?: HTMLInputElement) {
    if (this.type !== 'number') {
      this.onChange(selectedValue);
      return;
    }
    if (input?.validity?.badInput) {
      return;
    }
    this._lastNumber = toNumber(selectedValue);
    this.onChange(this._lastNumber);
  }

  override writeValue(value: any): void {
    super.writeValue(value);
    if (this.type === 'number') {
      this._lastNumber = toNumber(value);
    }
  }

  /** Leaving the input touches the form's control: its errors show from then on. */
  onBlur(input?: HTMLInputElement): void {
    if (this.type === 'number' && input?.validity?.badInput) {
      // Left unfinished: back to the number the form holds
      this.internalFormControl.setValue(this._lastNumber === null ? '' : String(this._lastNumber), { emitEvent: false });
    }
    this.normalize();
    this.checkError();
    this.onTouched();
  }

  /** A number left: whole when integer, within min/max with `clampToBounds`, as nz-input-number gave it. */
  private normalize(): void {
    const value = toNumber(this.internalFormControl?.value);
    if (this.type !== 'number' || value === null) {
      return;
    }
    let normalized = this.numericMode === 'integer' ? Math.round(value) : value;
    if (this.clampToBounds) {
      normalized = Math.min(this.max ?? normalized, Math.max(this.min ?? normalized, normalized));
    }
    if (normalized !== value) {
      this.internalFormControl.setValue(String(normalized), { emitEvent: false });
      this.onValueChange(normalized);
    }
  }

  onPrefixSelectChange(selectedValue: any) {
    this.prefixSelectedValue = selectedValue;
    this.onChange(selectedValue);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  restrictInput(event: KeyboardEvent): void {
    this.restrictDecimal(event);
    this.restrictPercentageInput(event);
  }

  restrictDecimal(event: KeyboardEvent): void {
    if (this.numericMode === 'integer' && (event.key === '.' || event.key === ',')) {
      event.preventDefault();
    }
  }
  restrictPercentageInput(event: KeyboardEvent): void {
    if (this.numericMode === 'percentage') {
      const inputChar = event.key;
      const currentValue = (event.target as HTMLInputElement).value + inputChar;
      const parsedValue = parseFloat(currentValue);
      if (isNaN(parsedValue) || parsedValue < 0 || parsedValue > 100) {
        event.preventDefault();
      }
    }
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  // Propagate disabled state from parent control
  setDisabledState(isDisabled: boolean): void {
    // NOTE: do not pass { emitEvent: false } here. NG-ZORRO's nz-input-group
    // recomputes its `ant-input-affix-wrapper-disabled` class only when the
    // inner input's ngControl fires statusChanges. Suppressing the event leaves
    // the wrapper painted gray/disabled while the input itself is editable.
    if (isDisabled) {
      this.internalFormControl?.disable();
    } else {
      this.internalFormControl?.enable();
    }
  }
}
function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
}

export type NumericMode = 'decimal' | 'integer' | 'percentage' | 'currency';
